import type { Offer, Outcome } from '../../../market-widget/src/shared/types'
import { offeringApiHost } from '../../../market-widget/src/shared/offeringApiHosts'
import { create } from 'zustand'
import {
  calculateParlayOdds,
  combinedDecimalOdds,
  convertDecimalToAmerican,
  formatOdds,
  parseAmericanOdds,
} from '../utils/odds'
import {
  getExecutionRecipe,
  getPrimaryAction,
  getSelectionProviderKey,
  getSelections,
  selectionPresentation,
  selectionSetCombinedOdds,
  selectionSetHasCorrelatedLegs,
} from '../utils/selectionSet'
import type { Selection, SelectionSet } from '../types/feedContract'

const UNSIGNED_INTEGER_PATTERN = /^\d+$/

export type OddsComparison = 'increased' | 'decreased' | 'unchanged' | null
export type SuspendedStatus = 'none' | 'partial' | 'full'
export type ClosedStatus = 'none' | 'partial' | 'full'

export interface LegOddsData {
  /** Full sibling prices from the same offering read; used by market insights. */
  offer?: Offer
  /** The selected outcome already returned by the offering read. */
  outcome?: KambiOutcome
  sport?: string
  league?: string
  outcomeId: string
  currentOdds: string | null
  /** The exact decimal price behind `currentOdds`, for hosts that show decimal odds. */
  currentDecimal?: number | null
  originalOdds: string | null
  comparison: OddsComparison
  isSuspended: boolean
  isClosed: boolean
  isLive: boolean
}

export interface ActivityOddsData {
  updatedOdds: string | null
  /** The exact decimal price behind `updatedOdds`. */
  updatedDecimal?: number | null
  oddsComparison: OddsComparison
  originalOdds?: string
  legs: Record<string, LegOddsData>
  suspendedStatus: SuspendedStatus
  closedStatus: ClosedStatus
  isLoading: boolean
  lastFetchTimestamp: number
  /** Time of the successful offering read that supplied legs and their offers. */
  lastSuccessfulFetchTimestamp: number
}

export interface KambiRuntimeConfig {
  environment?: string
  lang?: string
  market?: string
  production?: boolean
}

interface OddsStore {
  oddsUpdates: Record<string, ActivityOddsData>
  fetchAndProcessOdds: (
    activityId: string,
    selectionSet: SelectionSet,
    offering: string,
    runtime?: KambiRuntimeConfig,
  ) => Promise<void>
  clearOddsUpdate: (activityId: string) => void
  getOddsForActivity: (activityId: string) => ActivityOddsData | null
}

interface KambiEvent {
  id: string | number
  state?: string
  sport?: string
  group?: string
}

export interface KambiOutcome extends Outcome {
  id: string | number
  odds?: number
  oddsAmerican?: string | number
  status?: string
  cashOutStatus?: string
  isLive?: boolean
  participantId?: string | number
  eventParticipantId?: string | number
}

interface KambiBetOffer extends Offer {
  id: string | number
  suspended?: boolean
  cashOutStatus?: string
  outcomes?: KambiOutcome[]
}

interface KambiOfferingResponse {
  events?: KambiEvent[]
  betOffers?: KambiBetOffer[]
}

interface KambiOnDemandResponse {
  selectedOdds?: {
    american?: string | number
    decimal?: string | number
  }
}
interface KambiPrice {
  american: string
  decimal: number | null
}

interface PricedSelection {
  selection: Selection
  outcomeId: string
  eventId: string
  originalOdds: string | null
}

interface SameGameGroup {
  key: string
  eventId: string
  outcomeIds: string[]
}

const KAMBI_API_VERSION = 'v2018'
const DEBOUNCE_MS = 5000
export const ODDS_REQUEST_TIMEOUT_MS = 10_000
const DEFAULT_LANG = 'en_US'
const DEFAULT_MARKET = 'US'
let nextOddsRequestId = 0
const activeOddsRequests = new Map<string, { id: number; controller: AbortController }>()

function hostRuntimeConfig(): KambiRuntimeConfig {
  if (typeof window === 'undefined') return {}
  const host = window as Window & {
    _kc?: { environment?: string; locale?: string; market?: string }
  }
  return {
    environment: host._kc?.environment,
    lang: host._kc?.locale,
    market: host._kc?.market,
  }
}

export function getKambiBaseUrl(offering: string, runtime: KambiRuntimeConfig = {}): string {
  const environment = runtime.environment?.trim().toLowerCase()
  let production = runtime.production ?? import.meta.env.PROD
  if (environment === 'ctn' || environment === 'cts') production = false
  if (environment === 'production' || environment === 'prod') production = true
  const encodedOffering = encodeURIComponent(offering.trim())
  if (!production) {
    const nonProductionEnvironment = environment === 'cts' ? 'cts' : 'ctn'
    return `https://${nonProductionEnvironment}-api.kambi.com/offering/${KAMBI_API_VERSION}/${encodedOffering}`
  }
  const subdomain = offeringApiHost(offering)
  return `https://${subdomain}.offering-api.kambicdn.com/offering/${KAMBI_API_VERSION}/${encodedOffering}`
}

function normalizeAmericanOdds(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const normalized = String(value).trim()
  if (!normalized) return null
  if (normalized.toUpperCase() === 'EVEN') return '+100'
  if (normalized.startsWith('+') || normalized.startsWith('-')) return normalized
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) return null
  return numeric >= 0 ? `+${normalized}` : normalized
}

function decimalFromAmerican(american: string): number | null {
  const value = parseAmericanOdds(american)
  if (value == null || value === 0) return null
  return value > 0 ? value / 100 + 1 : 100 / Math.abs(value) + 1
}

function kambiPrice(americanValue: unknown, milliDecimalValue: unknown): KambiPrice | null {
  const milliDecimal = Number(milliDecimalValue)
  const decimal = Number.isFinite(milliDecimal) && milliDecimal > 1000
    ? milliDecimal / 1000
    : null
  const american = normalizeAmericanOdds(americanValue)
    || (decimal ? convertDecimalToAmerican(decimal) : null)
  if (!american) return null
  return { american, decimal: decimal || decimalFromAmerican(american) }
}

function outcomePrice(outcome: KambiOutcome): KambiPrice | null {
  return kambiPrice(outcome.oddsAmerican, outcome.odds)
}

function compareOdds(current: string | null, original: string | null): OddsComparison {
  const currentValue = parseAmericanOdds(current)
  const originalValue = parseAmericanOdds(original)
  if (currentValue == null || originalValue == null) return null
  if (currentValue > originalValue) return 'increased'
  if (currentValue < originalValue) return 'decreased'
  return 'unchanged'
}

function statusFromCount(count: number, total: number): 'none' | 'partial' | 'full' {
  if (total > 0 && count === total) return 'full'
  return count > 0 ? 'partial' : 'none'
}

function pricedSelections(selectionSet: SelectionSet): PricedSelection[] {
  return getSelections(selectionSet)
    .filter((selection) => getSelectionProviderKey(selection) === 'kambi')
    .map((selection) => {
      const presentation = selectionPresentation(selection)
      return {
        selection,
        outcomeId: presentation.outcomeId,
        eventId: String(selection.provider_ref?.event_id || ''),
        originalOdds: presentation.odds == null ? null : formatOdds(presentation.odds),
      }
    })
    .filter((selection) => UNSIGNED_INTEGER_PATTERN.test(selection.outcomeId))
}

function sameGameGroups(selectionSet: SelectionSet, selections: PricedSelection[]): SameGameGroup[] {
  const bySelectionId = new Map(selections.map((item) => [item.selection.id, item]))
  const byOutcomeId = new Map(selections.map((item) => [item.outcomeId, item]))
  const groups: SameGameGroup[] = []
  const seen = new Set<string>()

  const append = (key: string, members: PricedSelection[]) => {
    const eventId = members[0]?.eventId
    const outcomeIds = members.map((member) => member.outcomeId)
    if (!eventId || outcomeIds.length < 2 || members.some((member) => member.eventId !== eventId)) return
    const identity = `${eventId}:${[...outcomeIds].sort().join(',')}`
    if (seen.has(identity)) return
    seen.add(identity)
    groups.push({ key, eventId, outcomeIds })
  }

  for (const group of selectionSet.groups || []) {
    if (group.kind !== 'same_game') continue
    append(group.id, group.selection_ids.map((id) => bySelectionId.get(id)).filter((item): item is PricedSelection => Boolean(item)))
  }

  const recipe = getExecutionRecipe(getPrimaryAction(selectionSet))
  for (const row of recipe?.rows || []) {
    if (row.type !== 'BET_BUILDER') continue
    append(`recipe:${row.index}`, row.outcome_ids.map((id) => byOutcomeId.get(String(id))).filter((item): item is PricedSelection => Boolean(item)))
  }

  return groups
}

async function fetchSameGamePrice(
  baseUrl: string,
  group: SameGameGroup,
  lang: string,
  market: string,
  signal: AbortSignal,
): Promise<KambiPrice | null> {
  const outcomePath = group.outcomeIds.map(encodeURIComponent).join(',')
  const params = new URLSearchParams({
    lang,
    market,
    excludeBetOffers: 'true',
    excludeCombinableOutcomeIds: 'true',
  })
  const response = await fetch(
    `${baseUrl}/onDemandPricing/event/${encodeURIComponent(group.eventId)}/outcome/${outcomePath}?${params.toString()}`,
    { signal },
  )
  if (!response.ok) return null
  const data = await response.json() as KambiOnDemandResponse
  return kambiPrice(data.selectedOdds?.american, data.selectedOdds?.decimal)
}

function combinedCurrentPrice(
  selections: PricedSelection[],
  legs: Record<string, LegOddsData>,
  currentPrices: Record<string, KambiPrice>,
  groups: SameGameGroup[],
  groupPrices: Map<string, KambiPrice | null>,
): KambiPrice | null {
  const covered = new Set<string>()
  const prices: KambiPrice[] = []

  for (const group of groups) {
    const price = groupPrices.get(group.key)
    if (!price) return null
    prices.push(price)
    group.outcomeIds.forEach((outcomeId) => covered.add(outcomeId))
  }

  for (const selection of selections) {
    if (covered.has(selection.outcomeId)) continue
    const leg = legs[selection.outcomeId]
    const price = currentPrices[selection.outcomeId]
    if (!price || !leg?.currentOdds || leg.isClosed || leg.isSuspended) return null
    prices.push(price)
  }

  if (prices.length === 0) return null
  if (prices.length === 1) return prices[0]
  const combinedDecimal = combinedDecimalOdds(prices.map((price) => price.decimal))
  if (combinedDecimal !== null) {
    const american = convertDecimalToAmerican(combinedDecimal)
    return american ? { american, decimal: combinedDecimal } : null
  }
  return { american: calculateParlayOdds(prices.map((price) => price.american)), decimal: null }
}

function originalCombinedOdds(selectionSet: SelectionSet, selections: PricedSelection[]): string | null {
  const authored = selectionSetCombinedOdds(selectionSet)
  if (authored) {
    const formatted = formatOdds(authored)
    if (formatted !== '--') return formatted
  }
  // Correlated legs are priced by the provider; multiplying their original
  // leg odds would fabricate the baseline every price comparison uses.
  if (selectionSetHasCorrelatedLegs(selectionSet)) return null
  const prices = selections.map((selection) => selection.originalOdds).filter((odds): odds is string => Boolean(odds && odds !== '--'))
  if (prices.length !== selections.length || prices.length === 0) return null
  return prices.length === 1 ? prices[0] : calculateParlayOdds(prices)
}

export const useOddsStore = create<OddsStore>((set, get) => ({
  oddsUpdates: {},

  fetchAndProcessOdds: async (activityId, selectionSet, offering, runtimeOverride) => {
    const existing = get().oddsUpdates[activityId]
    if (existing?.isLoading) return
    if (existing && Date.now() - existing.lastFetchTimestamp < DEBOUNCE_MS) return

    const selections = pricedSelections(selectionSet)
    const groups = sameGameGroups(selectionSet, selections)
    if (!activityId || !offering.trim() || selections.length === 0) return
    const requestId = ++nextOddsRequestId
    const controller = new AbortController()
    activeOddsRequests.set(activityId, { id: requestId, controller })
    const timeout = setTimeout(() => {
      controller.abort(new DOMException('Odds refresh timed out', 'TimeoutError'))
    }, ODDS_REQUEST_TIMEOUT_MS)

    set((state) => ({
      oddsUpdates: {
        ...state.oddsUpdates,
        [activityId]: {
          updatedOdds: state.oddsUpdates[activityId]?.updatedOdds || null,
          updatedDecimal: state.oddsUpdates[activityId]?.updatedDecimal ?? null,
          oddsComparison: state.oddsUpdates[activityId]?.oddsComparison || null,
          originalOdds: state.oddsUpdates[activityId]?.originalOdds,
          legs: state.oddsUpdates[activityId]?.legs || {},
          suspendedStatus: state.oddsUpdates[activityId]?.suspendedStatus || 'none',
          closedStatus: state.oddsUpdates[activityId]?.closedStatus || 'none',
          isLoading: true,
          lastFetchTimestamp: Date.now(),
          lastSuccessfulFetchTimestamp: existing?.lastSuccessfulFetchTimestamp ?? 0,
        },
      },
    }))

    try {
      const runtime = { ...hostRuntimeConfig(), ...runtimeOverride }
      const lang = runtime.lang?.trim() || DEFAULT_LANG
      const market = runtime.market?.trim() || DEFAULT_MARKET
      const baseUrl = getKambiBaseUrl(offering, runtime)
      // Every leg — grouped or not — needs its offering status: same-game
      // legs must still surface closed/suspended states and current leg
      // prices even though their combined price comes from on-demand pricing.
      const params = new URLSearchParams({ market, lang, includeParticipants: 'false' })
      for (const outcomeId of new Set(selections.map((selection) => selection.outcomeId))) {
        params.append('id', outcomeId)
      }
      // The deadline covers headers, bodies, and every same-game quote. Race
      // cancellation explicitly so an unresponsive transport cannot keep the
      // card in loading or allow its late result to overwrite the next refresh.
      const responses = Promise.all([
        fetch(`${baseUrl}/betoffer/outcome?${params.toString()}`, { signal: controller.signal })
          .then(async (response) => {
            if (!response.ok) throw new Error(`Kambi odds request failed: ${response.status}`)
            return await response.json() as KambiOfferingResponse
          }),
        Promise.all(groups.map(async (group): Promise<[string, KambiPrice | null]> => [
          group.key,
          await fetchSameGamePrice(baseUrl, group, lang, market, controller.signal),
        ])),
      ])
      const aborted = new Promise<never>((_, reject) => {
        const rejectAbort = () => reject(controller.signal.reason)
        if (controller.signal.aborted) rejectAbort()
        else controller.signal.addEventListener('abort', rejectAbort, { once: true })
      })
      const [data, groupPriceEntries] = await Promise.race([responses, aborted])
      const groupPrices = new Map<string, KambiPrice | null>(groupPriceEntries)
      const events = new Map((data.events || []).map((event) => [String(event.id), event]))
      const outcomes = new Map<string, { offer: KambiBetOffer; outcome: KambiOutcome }>()
      for (const offer of data.betOffers || []) {
        for (const outcome of offer.outcomes || []) {
          outcomes.set(String(outcome.id), { offer, outcome })
        }
      }

      const legs: Record<string, LegOddsData> = {}
      const currentPrices: Record<string, KambiPrice> = {}
      let closedCount = 0
      let suspendedCount = 0
      for (const selection of selections) {
        const match = outcomes.get(selection.outcomeId)
        const outcomeStatus = match?.outcome.status?.toUpperCase()
        // An outcome the offering no longer returns is gone from the book,
        // whether or not it belongs to a same-game group.
        const isClosed = outcomeStatus === 'CLOSED' || !match
        // Cash-out availability is deliberately NOT a suspension signal:
        // season futures price and settle normally with cash-out disabled.
        const isSuspended = !isClosed && Boolean(
          match?.offer.suspended
          || outcomeStatus === 'SUSPENDED',
        )
        const price = match ? outcomePrice(match.outcome) : null
        const currentOdds = price?.american || null
        if (price) currentPrices[selection.outcomeId] = price
        if (isClosed) closedCount++
        if (isSuspended) suspendedCount++
        legs[selection.outcomeId] = {
          outcomeId: selection.outcomeId,
          offer: match?.offer,
          outcome: match?.outcome,
          sport: events.get(selection.eventId)?.sport,
          league: events.get(selection.eventId)?.group,
          currentOdds,
          currentDecimal: price?.decimal ?? null,
          originalOdds: selection.originalOdds,
          comparison: compareOdds(currentOdds, selection.originalOdds),
          isSuspended,
          isClosed,
          isLive: Boolean(match?.outcome.isLive || events.get(selection.eventId)?.state === 'STARTED'),
        }
      }

      const updatedPrice = combinedCurrentPrice(selections, legs, currentPrices, groups, groupPrices)
      const updatedOdds = updatedPrice?.american ?? null
      const originalOdds = originalCombinedOdds(selectionSet, selections)

      if (activeOddsRequests.get(activityId)?.id !== requestId) return
      set((state) => ({
        oddsUpdates: {
          ...state.oddsUpdates,
          [activityId]: {
            updatedOdds,
            updatedDecimal: updatedPrice?.decimal ?? null,
            oddsComparison: compareOdds(updatedOdds, originalOdds),
            ...(originalOdds ? { originalOdds } : {}),
            legs,
            suspendedStatus: statusFromCount(suspendedCount, selections.length),
            closedStatus: statusFromCount(closedCount, selections.length),
            isLoading: false,
            lastFetchTimestamp: Date.now(),
            lastSuccessfulFetchTimestamp: Date.now(),
          },
        },
      }))
    } catch {
      if (activeOddsRequests.get(activityId)?.id !== requestId) return
      set((state) => ({
        oddsUpdates: {
          ...state.oddsUpdates,
          [activityId]: {
            ...(state.oddsUpdates[activityId] || {
              updatedOdds: null,
              oddsComparison: null,
              legs: {},
              suspendedStatus: 'none',
              closedStatus: 'none',
              lastFetchTimestamp: Date.now(),
              lastSuccessfulFetchTimestamp: 0,
            }),
            isLoading: false,
          },
        },
      }))
    } finally {
      clearTimeout(timeout)
      controller.abort()
      if (activeOddsRequests.get(activityId)?.id === requestId) activeOddsRequests.delete(activityId)
    }
  },

  clearOddsUpdate: (activityId) => {
    const request = activeOddsRequests.get(activityId)
    activeOddsRequests.delete(activityId)
    request?.controller.abort()
    set((state) => {
      const { [activityId]: _, ...rest } = state.oddsUpdates
      return { oddsUpdates: rest }
    })
  },

  getOddsForActivity: (activityId) => get().oddsUpdates[activityId] || null,
}))
